import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import type { DependenciesUploadStatus, LocalMediaState, PremiereClipInfo, PrprojUploadStatus } from '@mimir/client-common/fileSync/premiereTypes';
import Vue from 'vue';
import { getGlobalStore, appState } from '../store';
import type { ApplicationContext } from '@mimir/common/context/applicationContext';
import type { UploadItem } from '@mimir/client-common/upload/UploadItem';
import type { SearchResultHit } from '@mimir/common/dto/searchResultHit';
import type { ProgressInfo } from '@mimir/common/dto/progress';
import type { SafeHtmlBuilder } from '@mimir/common/util/safeHtmlBuilder';
import { showSimpleDialog } from '../dialogs/simple';
import { ImportNotifier } from './importNotifier';
import { updatePremiereClipInfo } from '../globals/premiere';
import { showToast, TOAST } from '@mimir/web/eventBus';


export class DefaultNotificationOps implements NotificationOps {

  constructor(private applicationContext: ApplicationContext) {}

  createNotifier(title: string | SafeHtmlBuilder, description: string | SafeHtmlBuilder, folderId: string | undefined) {
    return new ImportNotifier(this.applicationContext, title, description, folderId);
  }

  notifyStartRelinking(clipInfo: PremiereClipInfo) {
    Vue.set(clipInfo, 'isRelinking', true);
  }

  notifyStartRefreshingMediaState(mediaState: LocalMediaState) {
    Vue.set(mediaState, 'isStateRefreshing', true);
  }

  notifyStopRefreshingMediaState(mediaState: LocalMediaState) {
    Vue.set(mediaState, 'isStateRefreshing', false);
  }

  notifyTotalDependencies(dependenciesUploadStatus: DependenciesUploadStatus, totalDependencies: number) {
    Vue.set(dependenciesUploadStatus, 'totalDependencies', totalDependencies);
  }

  notifyUploadStart(dependenciesUploadStatus: DependenciesUploadStatus) {
    Vue.set(dependenciesUploadStatus, 'isUploading', true);
  }

  notifyUploadEnd(dependenciesUploadStatus: DependenciesUploadStatus) {
    Vue.set(dependenciesUploadStatus, 'isUploading', false);
  }

  notifyLocalProgressInfo(itemId: string | undefined): Promise<void> {
    return getGlobalStore().dispatch('injectProgressInfo', { itemId });
  }

  notifyPrprojUploadStatusMessage(prprojUploadStatus: PrprojUploadStatus, message: string) {
    Vue.set(prprojUploadStatus, 'message', message);
  }

  notifyItemUploadLockInfoChanged(uploadItemId: string): Promise<void> {
    return getGlobalStore().dispatch('getItemUploadLockInfo', uploadItemId);
  }

  notifyStartLocallyExportingItem(uploadItem: UploadItem): void {
    // Inject progressInfo for local render items
    appState.addLocallyExportingItem(uploadItem);
  }

  notifyStopLocallyExportingItem(uploadItem: UploadItem): void {
    appState.removeLocallyExportingItem(uploadItem);
  }

  notifyMajorOperationFailure(title: string, htmlMessage: SafeHtmlBuilder): void {
    showSimpleDialog({
      maxWidth: 450,
      icon: 'error',
      infoOnly: true,
      closeOnEscape: false,
      actionText: 'OK',
      header: title,
      baseHtml: htmlMessage
    });
  }

  notifySearchHitProgressInfo(uploadItemId: string, itemProgressInfo: ProgressInfo): void {
    const update: { id: string, update: Partial<SearchResultHit>} = {
      id: uploadItemId,
      update: {
        progressInfo: itemProgressInfo
      }
    };

    // Update local app state with canceled info
    getGlobalStore().commit('updateSearchHitById', update);
  }

  notifyPremiereClipInfoUpdated(ongoingUploads: UploadItem[], clipInfo: PremiereClipInfo) {
    updatePremiereClipInfo(ongoingUploads, clipInfo);
  }

  displayBriefError(msg: string): void {
    showToast(msg, TOAST.ERROR);
  }

  displayBriefSuccess(msg: string): void {
    showToast(msg, TOAST.SUCCESS);
  }

}
